import React from "react";
import { useState, useEffect, Fragment } from "react";
import RGL, { WidthProvider } from "react-grid-layout";
import "./widgets-layout.scss";
import { AiOutlinePlus } from "react-icons/ai";
import { FaPencilAlt } from "react-icons/fa";
import { MdOutlineDataThresholding } from "react-icons/md";
import { PiDotsSixVerticalLight } from "react-icons/pi";
import { TiDelete } from "react-icons/ti";

export interface IConfig {
  x: number,
  y: number,
  w: number,
  h: number,
  i: string,
  minH?: number,
  minW?: number,
  maxH?: number,
  maxW?: number
}

export interface IData {
  className: string,
  items: IBlock[],
  rowHeight: number,
  cols: number,
}

export interface ILayout {
  lock: boolean,
  layout: IConfig[],
  layoutItems: IBlock[],
}

export interface IStep {
  key: string,
  layout: IConfig[],
  layoutItems: IBlock[],
  name: string
}

export interface ISettings {
  widget_type: string,
  source: string,
  source_id?: string,
  paired_datamodel_id?: string;
  paired_datamodel_type?: string;
  paired_datamodel?: string;
  selectedColumns: string[],
  selectedThreshold?: string | undefined,
  timeframe?: string | undefined,
  dataset?: string | undefined,
  dataset_id?: string | undefined,
  customColumnsColors?: any, // eslint-disable-line
  show_only_for_threshold?: boolean
}

export interface IBlock {
  optionName: string,
  id: string,
  settings: ISettings | undefined,
  steps: IStep[],
  record_type?: string,
  info_type?: string,
  text?: string,
  selectedColumnsPaired?: string[],
  selectedColumns?: string[],
  textType?: string,
  source?: string,
  timeframe?: string | undefined,
  dataset?: string | undefined,
  dataset_id?: string | undefined,
  selectedStepId?: string,
  textType_label?: string,
  read_only?: boolean, 
  is_required_status?: boolean, 
  multi_line?: string
  case_reference?: string,
  widgetLayoutSettings?: any, // eslint-disable-line
  case_reference_id?: string
  event_field?: string,
  event_field_id?: string
}

export interface IOption {
  component: (id: string, layoutItemsLoc: IBlock[], layoutLoc: IConfig[], tabsWidgetData?: IBlock[]) => JSX.Element | undefined,
  name: string,
  settings?: ISettings,
  editable?: boolean,
  displayName?: string
}

export interface IProps {
  lock: boolean,
  optionsBlocks: IOption[],
  layoutItems: IBlock[],
  unblockInput: boolean,
  layout: IConfig[],
  mainLayoutParent: IBlock[],
  tabsWidget?:boolean,
  mode?: string,
  minWidth?: number,
  addAfter?: string,
  rowHeight?: number,
  blockMargin?: [number, number],
  setModal: (toggle: boolean) => void,
  setModalDataFromBlock: (id: string, layoutItems: IBlock[], mainLayoutParent: IBlock[], newTabWidget: boolean, tabWidgetEdit?: string, addAfter?: boolean) => void | undefined,
  getLayoutItems: (blocks: IBlock[], i: string) => void,
  getLayoutChanges: (blocks: IConfig[]) => void
}

const ReactGridLayout = WidthProvider(RGL);

const WidgetsLayout = ({optionsBlocks, tabsWidget, addAfter, mode, rowHeight, blockMargin, minWidth, mainLayoutParent, unblockInput, lock, setModal, setModalDataFromBlock, layoutItems, getLayoutItems, getLayoutChanges, layout}: IProps) => {
  const [data, setData] = useState<IData>({ className: "layout", items: [], rowHeight: rowHeight? rowHeight : 15, cols: 36 });
  const [showControls, setShowControls] = useState("");
  const [isGettingResized, setIsGettingResized] = useState(false);

  useEffect(() => {
    setData({ ...data, items: layoutItems? layoutItems: [] });
  }, [layoutItems]);

  useEffect(() => {
    const event = document.createEvent("HTMLEvents");
    event.initEvent("resize", true, false);
    document.dispatchEvent(event);
  }, [layout]);

  const renderBlocks = () => {
    if (data.items.length !== 0) {
      return data.items.map((item: IBlock) => {
        const findMatchedBlock = optionsBlocks.find((option: IOption) => option.name === item.optionName);

        let isHistoryBlockOrUnlocked = false;
        if (item?.settings?.source === "history" && mode !== "pdf" || item?.settings?.source === "model_performance") {
          isHistoryBlockOrUnlocked = true;
        } else if (!lock) {
          isHistoryBlockOrUnlocked = !lock;
        }

        let defineExtraClass = "";
        switch (findMatchedBlock?.displayName){
          case "Tabs widget":
            defineExtraClass = "tabsWidgetGridElem";
            break;
          case "Text":
            defineExtraClass = "textGridElem";
            break;
          default:
            defineExtraClass = "standartGridElem";
        }

        return (
          <div onMouseLeave={() => isHistoryBlockOrUnlocked && setShowControls("")} onMouseOver={() => isHistoryBlockOrUnlocked && setShowControls(item.id)} className={`basicLayout-blockWrapperMain ${isHistoryBlockOrUnlocked? "isHistoryBlockOrUnlocked" : ""} ${isGettingResized? "resizeStart" : ""} ${defineExtraClass}`} key={item.id}>

            <div className="absolutePlusWrapper">
              <span
                onClick={() => {
                  setModal(true);
                  setModalDataFromBlock(item.id, layoutItems, mainLayoutParent, false, "", true);
                }}
                className="absolutePlus"
              >
                <AiOutlinePlus />
              </span>
            </div>

            <div className={`basicLayout-blockWrapper ${item?.settings?.source === "history"? "historyWidget" : ""} ${item.optionName}`} key={item.id}>
              {(isHistoryBlockOrUnlocked && item.id === showControls) && (
                <Fragment>
                  {(unblockInput && !lock) && <span className={`basicLayout-draggableBtn drag-handle${tabsWidget ? "tabsWidget" : ""}`}>
                    <PiDotsSixVerticalLight/>
                  </span>}
                  <div className="basicLayout-wrapperBlockBtns">
                    {item.optionName === "Tabs Widget" && (
                      <span
                        onClick={() => {
                          setModal(true);
                          setModalDataFromBlock(item.id, layoutItems, mainLayoutParent, true);
                        }}
                        className="basicLayout-addBlockBtn"
                      >
                        <AiOutlinePlus />
                      </span>
                    )}
                    {findMatchedBlock?.editable && (
                      <div
                        className="basicLayout-editBlockBtn"
                        onClick={() => {
                          setModal(true);
                          const tabWidgetEdit = tabsWidget? mainLayoutParent[0].id : "";
                          setModalDataFromBlock(item.id, layoutItems, mainLayoutParent, false, tabWidgetEdit);
                        }}
                      >
                        {!unblockInput || (unblockInput && lock)? <MdOutlineDataThresholding /> : <FaPencilAlt /> }
                      </div>
                    )}
                    {(unblockInput && !lock) && <div className="basicLayout-deleteBlockBtn" onClick={() => deleteBlock(item.id)}>
                      <TiDelete />
                    </div>}
                  </div>
                </Fragment>
              )}
              {(item?.settings?.source_id && item.id !== showControls) && <div className="basicLayout-widgetBlockOuterLabel">{item?.settings?.source}</div>}
              {(item?.settings?.source === "history" && item.id !== showControls) && <div className="basicLayout-widgetBlockOuterLabel">Threshold = {item?.settings?.selectedThreshold}</div>}
              {findMatchedBlock && findMatchedBlock.component(item.id, layoutItems, layout, tabsWidget? mainLayoutParent : [] )}
            </div>
          </div>

        );
      });
    }
  };

  const deleteBlock = (i: string) => {
    const result = data.items.filter((el: IBlock) => el.id !== i);
    setData({ ...data, items: result? result : [] });
    getLayoutItems(result, i);
  };

  const layoutChangeHandler = (newLayoutState: IConfig[]) => {
    const newlyCreated = newLayoutState.filter(({ i: id1 }: IConfig) => !layout?.some(({ i: id2 }: IConfig) => id2 === id1));
    
    let constructLayout: IConfig[] = [...newLayoutState];
    let findPrevBlock: IConfig | undefined;

    if (newlyCreated.length !== 0) {
      constructLayout = constructLayout.map((block: IConfig) => {
        if (block.i === newlyCreated[0].i) {
          if (addAfter) {
            // pdf edditor functionality
            findPrevBlock = newLayoutState.find((lay: IConfig) => addAfter === lay.i);
          }

          const layoutItem = layoutItems.find((item: IBlock) => item.id === newlyCreated[0].i);
          const textType = layoutItem?.textType;
          let defaultH = 6;
          if (mode === "pdf" && (textType === "header" || textType === "subheader")) {
            defaultH = 2;
          }

          return { ...block, h: defaultH, w: minWidth ? minWidth : 13, minW: minWidth ? minWidth : 0 };
        }
        return block;
      });
    }

    if (findPrevBlock) {
      const prevBlockHeightWithRows = findPrevBlock.y + findPrevBlock.h;
      constructLayout = constructLayout.map((block:IConfig) => {
        if (block.i === newlyCreated[0].i) {
          // place the new one using amount of rows + height of the prev block
          return { ...block, y: prevBlockHeightWithRows };
        } else if (block.i !== newlyCreated[0].i && block.y >= prevBlockHeightWithRows) {
          // all rows which placed after should be pushed
          return { ...block, y: block.y + prevBlockHeightWithRows };
        } else {
          return block;
        }
      });
    }
    if (mode === "pdf") {
      getLayoutChanges(constructLayout);
    } else {
      (layoutItems.length !== 0 || mainLayoutParent.length !== 0) && getLayoutChanges(constructLayout);
    }
  };

  const onResize = (layoutItemCurrent: any, oldLayoutItem: any, layoutItem: any) => { // eslint-disable-line 
    if (layoutItem.h < 3) {
      layoutItem.h = 2;
      layoutItem.minH = 2;
    }
  };

  return (
    <div className={`basicLayout ${!lock ? "unlocked" : "locked"}`}>
      <div>
        {data.items.length !== 0 ? (
          <ReactGridLayout
            draggableHandle={`.drag-handle${tabsWidget ? "tabsWidget" : ""}`}
            isDroppable={!lock ? true : false}
            layout={layout}
            onResize={onResize}
            margin={blockMargin && blockMargin}
            isResizable={!lock ? true : false}
            isDraggable={!lock ? true : false}
            onLayoutChange={(layoutProp: IConfig[]) => layoutChangeHandler(layoutProp)}
            onResizeStart={() => mode === "pdf" && setIsGettingResized(true)}
            onResizeStop={() => mode === "pdf" &&  setIsGettingResized(false)}
            {...data}
          >
            {renderBlocks()}
          </ReactGridLayout>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default WidgetsLayout;
